import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ProductService,
  Product,
  CustomerProductPrice,
  SelectedCustomerService,
  PriceService,
  PriceConfigService
} from 'gung-standard';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, first, switchMap } from 'rxjs';
import { ColmecConfigurableHelperService } from '../../../services/colmec-configurable-helper.service';
import { ColmecCartService } from '../../../services/colmec-cart.service';

const buildExtraObjectWithValue = (value: any, pathStr: string): any => {
  const path = pathStr.split('.');
  const recursive = (current: {}, pathArr: string[], theValue: any) => {
    const [firstEle, ...rest] = pathArr;
    if (!firstEle) {
      return theValue;
    }
    current[firstEle] = recursive({}, rest, theValue);
    return current;
  };
  return recursive({}, path, value);
};

interface Alert {
  type: string;
  message: string;
}

@Component({
    selector: 'colmec-rim-options-modal',
    templateUrl: './colmec-rim-options-modal.component.html',
    styleUrls: ['./colmec-rim-options-modal.component.scss'],
    standalone: false
})
export class ColmecRimOptionsModalComponent implements OnInit, OnDestroy {
  alertMessage: Alert;

  @Input()
  rimIds: string[];

  @Input()
  parentProductId: string;

  @Input()
  assemblyProductsIds: string[];

  selectedAssembly: string;
  selectedRim: string;
  mapAssemblyProductPrice: Map<string, { product: Product; customerProductPrice: CustomerProductPrice }> = new Map();
  assemblyProductPriceArray: {
    key: string;
    value: { product: Product; customerProductPrice: CustomerProductPrice };
  }[] = [];

  protected unsubscribe: Subject<void> = new Subject();
  public hidePrice = false;

  constructor(
    public activeModal: NgbActiveModal,
    protected cartService: ColmecCartService,
    protected configurableHelperService: ColmecConfigurableHelperService,
    protected productService: ProductService,
    private selectedCustomerService: SelectedCustomerService,
    private priceService: PriceService,
    private priceConfigService: PriceConfigService,
    protected changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
    this.cartService
      .getCurrentCart()
      .pipe(first())
      .subscribe(cartRows => {
        const parentProductCart = cartRows.find(row => row.productId === this.parentProductId);
        if (parentProductCart && parentProductCart.extra.configurable) {
          this.selectedRim = parentProductCart.extra.configurable.rim;
          this.configurableHelperService.setSelectedRim(this.selectedRim);
          this.selectedAssembly = parentProductCart.extra.configurable.assembly;
        }
      });

    this.configurableHelperService
      .getSelectedRim()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(selectedRim => {
        if (selectedRim) {
          this.selectedRim = selectedRim;
        }
      });

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        switchMap(selectedCustomer =>
          forkJoin({
            products: this.productService.getProductsByIds(this.assemblyProductsIds).pipe(first()),
            customerProductPrices: this.priceService
              .getCustomerPrices(this.assemblyProductsIds, selectedCustomer.id)
              .pipe(first())
          })
        )
      )
      .subscribe(result => {
        this.mapAssemblyProductPrice = new Map();
        result.products.forEach(product => {
          this.mapAssemblyProductPrice.set(product.id, {
            product,
            customerProductPrice: result.customerProductPrices.find(c => c.productId === product.id)
          });
        });

        this.assemblyProductPriceArray = Array.from(this.mapAssemblyProductPrice.entries()).map(([key, value]) => ({
          key,
          value
        }));

        if (!this.selectedAssembly && this.assemblyProductsIds && this.assemblyProductsIds.length > 0) {
          this.selectedAssembly = this.assemblyProductPriceArray[0].key;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  ngOnDestroy() {
    this.configurableHelperService.setSelectedRim(null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onAdd() {
    if (!this.selectedRim) {
      this.alertMessage = {
        message: 'RIM_REQUIRED',
        type: 'danger'
      };
      return;
    }
    this.cartService.setConfiguredRim(this.selectedRim, this.parentProductId);
    this.cartService.setConfiguredAssembly(this.selectedAssembly, this.parentProductId);
    this.activeModal.close('Ok click');
  }

  onClear() {
    this.cartService.setConfiguredRim(null, this.parentProductId);
    this.cartService.setConfiguredAssembly(null, this.parentProductId);
    this.activeModal.dismiss('cancel click');
  }
}
